import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import feather from 'feather-icons';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Alert from '../components/Alert';
import CategoryExpenseBox from '../components/Category/CategoryExpenseBox';
import Loader from '../components/Loader';
import BottomNavbar from '../components/Home/BottomNavbar';
import CategoryDonutChart from '../components/Charts/CategoryDonutChart';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import BackButton from '../components/BackButton';
import { getUserTimezone, amountDisplay } from '../util/helper';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';
import { FiPieChart } from 'react-icons/fi';
import '../styles/CategoryExpensePage.css';

const CategoryExpense = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const timeZone = getUserTimezone();
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const [searchParams, setSearchParams] = useSearchParams();
    const month = searchParams.get('month');
    const initialReportingType = searchParams.get('type') === 'yearly' ? 'yearly' : 'monthly';
    const [dataMonth, setDataMonth] = useState(month || null);
    const [reportingType, setReportingType] = useState(initialReportingType);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categoryWiseExpense, setCategoryWiseExpense] = useState([]);
    const [dataWiseCategoryWiseExpense, setDataWiseCategoryWiseExpense] = useState(null);
    const [daysInMonth, setDaysInMonth] = useState(null);
    const [totalExpense, setTotalExpense] = useState(0);
    const [customMonth, setCustomMonth] = useState('');
    const [key, setKey] = useState(null);
    const requestIdRef = useRef(0);
    const currentPeriodStart = dataMonth
        ? dayjs(dataMonth).tz(timeZone)
        : dayjs()
              .tz(timeZone)
              .startOf(reportingType === 'yearly' ? 'year' : 'month');
    const isYearly = reportingType === 'yearly';
    const syncSearchParams = (nextMonth, nextReportingType) => {
        setSearchParams({ type: nextReportingType, month: nextMonth });
    };

    const handlePrevPeriod = (e) => {
        e.preventDefault();
        const previousPeriodStart = currentPeriodStart
            .subtract(1, isYearly ? 'year' : 'month')
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');
        setDataMonth(previousPeriodStart);
        syncSearchParams(previousPeriodStart, reportingType);
    };

    const handleCurrentPeriod = (e) => {
        e?.preventDefault?.();
        const periodStart = dayjs()
            .tz(timeZone)
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');

        setDataMonth(periodStart);
        syncSearchParams(periodStart, reportingType);
    };

    const handlePeriodTypeChange = (nextType) => {
        const nextPeriodStart = dayjs()
            .tz(timeZone)
            .startOf(nextType === 'yearly' ? 'year' : 'month')
            .format('YYYY-MM-DD');

        setReportingType(nextType);
        setDataMonth(nextPeriodStart);
        setCustomMonth('');
        syncSearchParams(nextPeriodStart, nextType);
    };

    const handleApplyCustomMonth = () => {
        const start = dayjs(customMonth)
            .tz(timeZone)
            .startOf(isYearly ? 'year' : 'month')
            .format('YYYY-MM-DD');

        setDataMonth(start);
        syncSearchParams(start, reportingType);

        const modalEl = document.getElementById('period');
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        modalInstance?.hide();
    };

    const categoryWiseExpenseData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        try {
            setLoading(true);
            setError('');
            let uri;
            if (dataMonth) {
                uri = `/api/user/category-wise-expense?month=${dataMonth}&type=${reportingType}`;
            } else {
                uri = `/api/user/category-wise-expense?type=${reportingType}`;
            }
            const response = await serverClient.get(uri);

            // Ignore stale responses from older requests to avoid rendering wrong month data.
            if (requestId !== requestIdRef.current) {
                return;
            }

            const categoryExpense = response?.data?.categoryExpense;
            const dateWiseCategoryWiseExpense = response?.data?.dateWiseCategoryWiseExpense;

            setCategoryWiseExpense(
                Array.isArray(categoryExpense)
                    ? categoryExpense.map((expense) => ({
                          ...expense,
                          total_amount: Number(expense.total_amount || 0),
                          progress: Number(expense.progress || 0),
                      }))
                    : []
            );
            setDataWiseCategoryWiseExpense(
                dateWiseCategoryWiseExpense && typeof dateWiseCategoryWiseExpense === 'object'
                    ? dateWiseCategoryWiseExpense
                    : null
            );
            setDaysInMonth(
                Number.isFinite(response?.data?.daysInMonth) ? response.data.daysInMonth : null
            );
            setTotalExpense(Number(response?.data?.totalExpense || 0));
            setKey(
                `${response?.data?.reportingType || reportingType}-${response?.data?.month || dataMonth || 'current'}`
            );
        } catch (e) {
            if (requestId !== requestIdRef.current) {
                return;
            }
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(e.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [dataMonth, navigate, reportingType, t]);

    useEffect(() => {
        feather.replace();
        categoryWiseExpenseData();
    }, [categoryWiseExpenseData]);

    useRefreshSignal(refreshSignal, () => {
        categoryWiseExpenseData();
    });

    const categoryCount = categoryWiseExpense.length;
    const topCategory =
        categoryWiseExpense.length > 0
            ? [...categoryWiseExpense].sort((a, b) => b.total_amount - a.total_amount)[0]
            : null;
    const topCategoryAmount = topCategory ? topCategory.total_amount : 0;
    const emptyPeriodLabel = isYearly
        ? currentPeriodStart.format('YYYY')
        : currentPeriodStart.format('MMM YYYY');

    return (
        <>
            <Helmet>
                <title>{getPageMeta('categoryExpense', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('categoryExpense', language).description}
                />
                <meta name="keywords" content={getPageMeta('categoryExpense', language).keywords} />
                <link rel="canonical" href={getPageMeta('categoryExpense', language).url} />
                {getPageMeta('categoryExpense', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:title"
                    content={getPageMeta('categoryExpense', language).title}
                />
                <meta
                    property="og:description"
                    content={getPageMeta('categoryExpense', language).description}
                />
                <meta property="og:url" content={getPageMeta('categoryExpense', language).url} />
                <meta
                    property="og:image"
                    content={getPageMeta('categoryExpense', language).image}
                />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={getPageMeta('categoryExpense', language).title}
                />
                <meta
                    name="twitter:description"
                    content={getPageMeta('categoryExpense', language).description}
                />
                <meta
                    name="twitter:image"
                    content={getPageMeta('categoryExpense', language).image}
                />
            </Helmet>
            <Loader loading={loading} />

            {error && (
                <div className="custom-container">
                    <div className="mb-2">
                        <Alert alert={'danger'}>{error}</Alert>
                    </div>
                </div>
            )}

            <div className="modal add-money-modal fade" id="period" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">{t('selectPeriod')}</h2>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="inputfromdate" className="form-label">
                                    {t('fromDate')}
                                </label>
                                <input
                                    type="month"
                                    className="form-control"
                                    id="inputfromdate"
                                    value={customMonth}
                                    onChange={(e) => setCustomMonth(e.target.value)}
                                />
                            </div>

                            <button
                                className="btn theme-btn successfully w-100 mt-4"
                                onClick={handleApplyCustomMonth}
                            >
                                {t('apply')}
                            </button>
                        </div>

                        <button type="button" className="btn close-btn" data-bs-dismiss="modal">
                            <i className="icon" data-feather="x"></i>
                        </button>
                    </div>
                </div>
            </div>

            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton fallback="/" />
                        <h2>{t('categoryExpense')}</h2>

                        <div className="dropdown">
                            <Link
                                to="#"
                                className="back-btn"
                                role="button"
                                data-bs-toggle="dropdown"
                            >
                                <i className="icon" data-feather="settings"></i>
                            </Link>

                            <ul className="dropdown-menu">
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'monthly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handlePeriodTypeChange('monthly')}
                                    >
                                        {t('monthly')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={`dropdown-item${reportingType === 'yearly' ? ' fw-semibold text-primary' : ''}`}
                                        onClick={() => handlePeriodTypeChange('yearly')}
                                    >
                                        {t('yearly')}
                                    </button>
                                </li>
                                <li>
                                    <hr className="dropdown-divider" />
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to=""
                                        onClick={handleCurrentPeriod}
                                    >
                                        {isYearly ? t('thisYear') : t('thisMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="#"
                                        onClick={handlePrevPeriod}
                                        className="dropdown-item"
                                    >
                                        {isYearly ? t('lastYear') : t('lastMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#period"
                                        data-bs-toggle="modal"
                                    >
                                        {t('custom')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <div className="category-expense-page">
                <section>
                    <div className="custom-container">
                        <div className="ce-hero">
                            <div className="ce-hero-top">
                                <div>
                                    <p className="ce-hero-label">{t('totalExpense')}</p>
                                    <h2 className="ce-hero-value">
                                        {amountDisplay(totalExpense, language)}
                                    </h2>
                                </div>
                                <div className="ce-hero-icon">
                                    <FiPieChart />
                                </div>
                            </div>
                            {categoryCount > 0 && (
                                <div className="ce-hero-meta">
                                    <div className="meta-item">
                                        <h6>{t('categories')}</h6>
                                        <b>{categoryCount}</b>
                                    </div>
                                    {topCategory && (
                                        <div className="meta-item">
                                            <h6>{t('topCategory')}</h6>
                                            <b>{topCategory.category_name}</b>
                                        </div>
                                    )}
                                    {topCategory && (
                                        <div className="meta-item">
                                            <h6>{t('topCategoryExpenseAmount')}</h6>
                                            <b>{amountDisplay(topCategoryAmount)}</b>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {categoryWiseExpense.length > 0 && (
                    <CategoryDonutChart
                        key={key}
                        month={dataMonth}
                        categoryExpense={categoryWiseExpense}
                        data={dataWiseCategoryWiseExpense}
                        daysInMonth={daysInMonth}
                        reportingType={reportingType}
                        language={language}
                    />
                )}

                <section className="section-b-space">
                    <div className="custom-container">
                        {categoryWiseExpense.length > 0 && (
                            <h4 className="ce-section-label">{t('categoryWiseExpense')}</h4>
                        )}
                        <div className="row gy-3">
                            {categoryWiseExpense.length > 0 ? (
                                categoryWiseExpense.map((expense, index) => (
                                    <div className="col-6" key={expense.id || `expense-${index}`}>
                                        <CategoryExpenseBox
                                            expense={expense}
                                            currentMonth={currentPeriodStart.format('YYYY-MM-DD')}
                                            reportingType={reportingType}
                                        />
                                    </div>
                                ))
                            ) : !loading ? (
                                <div className="col-12">
                                    <div className="ce-empty-state">
                                        <div className="empty-state-layout">
                                            <div className="empty-state-content">
                                                <div className="empty-state-head">
                                                    <div className="empty-state-copy">
                                                        <div className="empty-state-pill">
                                                            {emptyPeriodLabel}
                                                        </div>
                                                        <h3 className="empty-state-title">
                                                            {t('noExpenses')}
                                                        </h3>
                                                        <p className="empty-state-text">
                                                            {isYearly
                                                                ? t('noExpenseThisYear')
                                                                : t('noExpenseThisMonth')}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="empty-state-overview">
                                                    <div className="empty-state-metric">
                                                        <span className="metric-label">
                                                            {t('expense')}
                                                        </span>
                                                        <strong className="metric-value">
                                                            {amountDisplay(0, language)}
                                                        </strong>
                                                    </div>
                                                    <div className="empty-state-metric">
                                                        <span className="metric-label">
                                                            {t('categories')}
                                                        </span>
                                                        <strong className="metric-value">0</strong>
                                                    </div>
                                                </div>

                                                <div className="empty-state-actions">
                                                    <button
                                                        type="button"
                                                        className="btn theme-btn successfully"
                                                        onClick={handleCurrentPeriod}
                                                    >
                                                        {isYearly ? t('thisYear') : t('thisMonth')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#period"
                                                    >
                                                        {t('selectPeriod')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </div>

            <BottomNavbar />
        </>
    );
};

export default CategoryExpense;
